import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { ComponentProps, type JSX, useEffect, useMemo, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import ReportLocation from '@alltrails/amplitude/enums/ReportLocation';
import UserLabel from '@alltrails/cards/components/UserLabel';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import compareUserIds from '@alltrails/core/utils/compareUserIds';
import getActivityType from '@alltrails/core/utils/getActivityType';
import Link from '@alltrails/denali/components/Link';
import Toast from '@alltrails/denali/components/Toast';
import Check from '@alltrails/denali/icons/Check';
import type { LightboxPhoto, LightboxUser } from '@alltrails/denali/types';
import { useExperiments } from '@alltrails/experiments';
import { EntryPoint } from '@alltrails/reporting/types';
import { User as BelayUser } from '@alltrails/types/belay';
import type Context from '@alltrails/types/Context';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import type { Review } from '@alltrails/types/Review';
import type { ReviewPhoto } from '@alltrails/types/ReviewPhoto';
import type SuccessToast from '@alltrails/types/SuccessToast';
import type { User } from '@alltrails/types/User';
import { useCreateReplyMutation, useDeleteReplyMutation, useDeleteReviewMutation, useUpdateReplyMutation } from '../../slices/reviewsApi';
import { TrailReview } from '../TrailReview';
import TrailReviewDate from '../TrailReview/TrailReviewDate';
import styles from './ReviewItem.module.scss';

const ReportIssueModal = dynamic(() => import('@alltrails/reporting/components/ReportIssueModal'), { ssr: false });
const Dialog = dynamic(() => import('@alltrails/denali/components/Dialog'), { ssr: false });

const messages = defineMessages({
  DELETE_REVIEW: { defaultMessage: 'Delete review?' },
  DELETING_REVIEW_SUBTEXT: { defaultMessage: 'Deleting a review will erase it permanently.' },
  DELETE: { defaultMessage: 'Delete' },
  KEEP: { defaultMessage: 'Keep' }
});

export type ReviewItemProps = {
  review: Review;
  index: number;
  languageRegionCode: LanguageRegionCode;
  onActivityLinkClick?: (review: Review) => void;
  includeTrailName?: boolean;
  onDeleteReview?: (review: Review) => void;
  onLightboxClose?: () => void;
  onLightboxPhotoViewed?: (review: Review, photo: LightboxPhoto['photo']) => void;
  onLightboxUserClick?: (user: LightboxUser) => void;
  onReviewPhotosScroll?: (review: Review, numPhotos: number) => void;
  onReviewPhotosScrollEnd?: (review: Review, numPhotos: number) => void;
  openReviewFormModal?: (trailFormProps: any) => void;
  onUpdateReview?: (data: { detail: Review }) => void;
  onReviewPhotoClick?: (review: Review, photo: ReviewPhoto) => void;
  preferHTML?: boolean;
  photosClassName?: string;
  hideBorderBottom?: boolean;
  ReviewRenderer?: (props: ComponentProps<typeof TrailReview>) => JSX.Element;
  trailLinkNewTab?: boolean;
  onUserClick?: (user: User) => void;
  localizeOnLoad?: boolean;
  context: Context;
  analyticsReportLocation?: ReportLocation;
  onToggleSummaryExclusion?: (review: Review, summaryExclusion: boolean) => void;
  photosOverflowOnMobile?: boolean;
  bottomLinkType?: 'trail' | 'activity';
  onTrailLinkClick?: () => void;
  featuredReviewTruncation?: boolean;
  containerClassName?: string;
  onShowMoreClick?: () => void;
  showVoteButton?: boolean;
};

const ReviewItem = ({
  review,
  index,
  languageRegionCode,
  onActivityLinkClick,
  includeTrailName,
  onDeleteReview,
  onLightboxClose,
  onLightboxPhotoViewed,
  onLightboxUserClick,
  onReviewPhotosScroll,
  onReviewPhotosScrollEnd,
  openReviewFormModal,
  onUpdateReview,
  onReviewPhotoClick,
  preferHTML,
  photosClassName,
  hideBorderBottom,
  ReviewRenderer,
  trailLinkNewTab,
  onUserClick,
  localizeOnLoad = false,
  context,
  analyticsReportLocation,
  onToggleSummaryExclusion,
  photosOverflowOnMobile,
  bottomLinkType = 'activity',
  onTrailLinkClick,
  featuredReviewTruncation = false,
  containerClassName,
  onShowMoreClick,
  showVoteButton
}: ReviewItemProps) => {
  const {
    formattedDefaultMessages: { DELETE_REVIEW, DELETING_REVIEW_SUBTEXT, DELETE, KEEP }
  } = useFormatMessage(messages);
  const intl = useIntl();
  const isPlpBadgingEnabled = useExperiments()['web-tmt-rm-plp-badging']?.value === 'on';
  const [openAlertDialog, setIsAlertDialogOpen] = useState(false);
  const [openReportingModal, setIsReportingModalOpen] = useState(false);
  const [reportProps, setReportProps] = useState<{ trailId: string; reviewId: string; reportUser: User | BelayUser } | null>(null);
  const [reportingSuccessToast, setReportingSuccessToast] = useState<SuccessToast>({ isVisible: false, text: '' });
  const { hasPermission } = useAuthorization();
  const isManager = isPlpBadgingEnabled && review.isTrailManager;

  const [deleteReview] = useDeleteReviewMutation();
  const [createReply] = useCreateReplyMutation();
  const [updateReply] = useUpdateReplyMutation();
  const [deleteReply] = useDeleteReplyMutation();

  useEffect(() => {
    if (reportingSuccessToast.isVisible) {
      setTimeout(() => {
        setReportingSuccessToast({ isVisible: false, text: '' });
      }, 3000);
    }
  });

  const handleDeleteReviewClick = () => {
    setIsAlertDialogOpen(true);
  };

  const handleOnCloseDeleteReviewClick = () => {
    setIsAlertDialogOpen(false);
  };

  const onDeleteClick = () => {
    deleteReview({ userId: review.user.id, reviewId: review.id })
      .unwrap()
      .then(() => {
        onDeleteReview?.(review);
      })
      .catch((e: Error) => console.log(`Failed to delete review: ${e?.message}`));
    return false;
  };

  const handleOpenReviewFormModal = () => {
    openReviewFormModal?.(review);
  };

  const changeRepliesSuccessCallback = (xhr: any) => {
    if (onUpdateReview && xhr.trail_reviews && xhr.trail_reviews.length > 0) {
      onUpdateReview({ detail: xhr.trail_reviews[0] });
    }
  };

  const handleDeleteReplyClick = () => {
    const { replies } = review;
    if (replies && replies.length > 0) {
      deleteReply({ review })
        .unwrap()
        .then(response => {
          changeRepliesSuccessCallback(response);
        })
        .catch((e: Error) => console.log(`Failed to delete reply: ${e?.message}`));
    }
  };

  const handleReplySave = async (comment: string) => {
    const { replies } = review;
    if (replies && replies.length > 0) {
      await updateReply({ review, comment })
        .unwrap()
        .then(response => {
          changeRepliesSuccessCallback(response);
        })
        .catch((e: Error) => console.log(`Failed to update reply: ${e?.message}`));
    } else {
      await createReply({ review, comment })
        .unwrap()
        .then(response => {
          changeRepliesSuccessCallback(response);
        })
        .catch((e: Error) => console.log(`Failed to create reply: ${e?.message}`));
    }
  };

  const getReviewNameExtension = () => {
    const { trailName, trailSlug } = review;
    const showTrailName = includeTrailName && trailName && trailSlug;
    if (!isManager && (!includeTrailName || !showTrailName)) {
      return null;
    }

    return (
      <>
        {isManager && (
          <span className={classNames(styles.manager, { [styles.withTrailName]: showTrailName })}>
            <FormattedMessage defaultMessage="Land manager" />
          </span>
        )}
        {showTrailName && (
          <Link
            className={styles.nameExtension}
            href={`/trail/${trailSlug}`}
            {...(trailLinkNewTab ? { target: '_blank', rel: ['noopener', 'noreferrer'] } : {})}
            size="md"
          >
            {trailName}
          </Link>
        )}
      </>
    );
  };

  const onClickUser = onUserClick ? () => onUserClick(review.user) : undefined;

  const handleToggleSummaryExclusion = (summaryExclusion: boolean) => onToggleSummaryExclusion?.(review, summaryExclusion);

  const ItemRenderer = useMemo(() => ReviewRenderer || TrailReview, [ReviewRenderer]);

  const nameExtension = getReviewNameExtension() || undefined;
  const activityType = review.activity ? getActivityType(intl, review.activity.uid) : '';
  const activityText = activityType || review.activity?.name || '';

  const label = (
    <UserLabel
      userLabelClassName={classNames(styles.userLabel, { [styles.withNameExtension]: !!nameExtension })}
      nameExtension={nameExtension}
      nameExtensionClassName={styles.nameExtension}
      onClick={onClickUser}
      ratingValue={review.rating}
      secondaryText={
        <span suppressHydrationWarning>
          <TrailReviewDate created={review.metadata.created} />
        </span>
      }
      tertiaryText={activityText}
      user={review.user}
      flair={isManager ? 'manager' : undefined}
    />
  );

  return (
    <div
      className={classNames(
        styles.reviewContainer,
        {
          [styles.hideBorderBottom]: hideBorderBottom,
          [styles.newReviewContainer]: true
        },
        containerClassName
      )}
    >
      <ItemRenderer
        context={context}
        currentUser={context.currentUser}
        index={index}
        isEditable={compareUserIds(review.user.id, context?.currentUser?.id) || hasPermission({ permission: 'trails:manage' })}
        languageRegionCode={languageRegionCode}
        onActivityLinkClick={() => onActivityLinkClick?.(review)}
        onDeleteClick={handleDeleteReviewClick}
        onEditClick={handleOpenReviewFormModal}
        onLightboxClose={onLightboxClose}
        onLightboxPhotoViewed={onLightboxPhotoViewed}
        onLightboxUserClick={onLightboxUserClick}
        onReplySaveClick={handleReplySave}
        onReviewPhotoClick={onReviewPhotoClick}
        onReviewPhotosScroll={onReviewPhotosScroll}
        onReviewPhotosScrollEnd={onReviewPhotosScrollEnd}
        onReplyDeleteClick={handleDeleteReplyClick}
        onShowMoreClick={onShowMoreClick}
        onToggleSummaryExclusion={handleToggleSummaryExclusion}
        onTrailLinkClick={onTrailLinkClick}
        bottomLinkType={bottomLinkType}
        featuredReviewTruncation={featuredReviewTruncation}
        localizeOnLoad={localizeOnLoad}
        photosClassName={photosClassName}
        photosOverflowOnMobile={photosOverflowOnMobile}
        preferHTML={preferHTML}
        label={label}
        review={review}
        showVoteButton={showVoteButton}
        setReportProps={setReportProps}
        setReportingSuccessToast={setReportingSuccessToast}
        setIsReportingModalOpen={setIsReportingModalOpen}
      />
      {openAlertDialog && (
        <Dialog
          title={DELETE_REVIEW}
          body={DELETING_REVIEW_SUBTEXT}
          buttons={{
            primary: {
              onClick: () => {
                handleOnCloseDeleteReviewClick();
                onDeleteClick();
              },
              text: DELETE
            },
            flat: {
              onClick: handleOnCloseDeleteReviewClick,
              text: KEEP
            }
          }}
          onRequestClose={handleOnCloseDeleteReviewClick}
          size="sm"
        />
      )}
      {context && context?.currentUser && reportProps && setReportingSuccessToast && analyticsReportLocation && openReportingModal && (
        <ReportIssueModal
          analyticsData={{ location: analyticsReportLocation, contentType: 'review' }}
          currentUserId={context.currentUser.id}
          entryPoint={EntryPoint.Reviews}
          isOpen={openReportingModal}
          onCloseRequest={() => setIsReportingModalOpen(false)}
          reportId={reportProps.trailId}
          reportUser={reportProps.reportUser}
          reportType="issue"
          setSuccessToast={setReportingSuccessToast}
          trailReviewId={reportProps.reviewId}
        />
      )}
      {reportingSuccessToast.isVisible && (
        <Toast isOpen message={reportingSuccessToast.text} position="alternative" type="neutral" icon={{ Component: Check }} />
      )}
    </div>
  );
};

export default ReviewItem;
